/**
 * Admin-configurable pricing config, as a value.
 *
 * See `docs/adr/0003-pricing-config-as-parameter.md`. Every rate the engines
 * need now arrives as a parameter instead of being read off a module-level
 * constant, because the three surfaces that price a booking get their config
 * from three different places: the API server loads it live from Postgres, and
 * the booking app and dashboard fetch it once from the API at page load. A fixed
 * `RATE_CARDS` import only works while there is exactly one baked-in source of
 * truth, and the moment there isn't, the pressure is to reimplement the formula
 * somewhere the config *is* reachable — which is how the same numbers ended up
 * in five files the first time.
 *
 * Two properties this module is built to guarantee:
 *
 *   - **The default config is today's behaviour, exactly.** Every surge axis
 *     defaults to zero and Same-Day defaults to off, so threading a config
 *     parameter through the engines changes no price until an admin sets one.
 *     A refactor that quietly moves a total is indistinguishable from a pricing
 *     bug, and this one moves nothing.
 *   - **Nothing here reads the clock.** Not the current time, not the local
 *     timezone. See `docs/adr/0004-same-day-availability-server-side-only.md`.
 */

import { Cents, percentToBasisPoints } from "./money";
import {
  CLEANING_TYPE_IDS,
  CleaningTypeId,
  FLEXIBLE_DISCOUNT_CENTS,
  MAX_ROOMS,
  RateCard,
  RoomPrices,
  flatRoomPrices,
  getRateCard,
} from "./rates";

/**
 * Day-of-week keys, spelled out.
 *
 * Numeric weekday indices are the obvious alternative and the wrong one: 0 is
 * Sunday in JavaScript and Monday in half the world's calendars, and an admin
 * grid keyed by 0-6 is a grid whose meaning depends on who is reading it. The
 * order below is JavaScript's, so `DAYS_OF_WEEK[d.getUTCDay()]` is a valid
 * lookup — but the *stored* key is always the name.
 */
export type DayOfWeek =
  | "sunday"
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday";

export const DAYS_OF_WEEK: DayOfWeek[] = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

/** A value configured independently for each day of the week. */
export type PerDay<T> = Record<DayOfWeek, T>;

export function perDay<T>(value: T): PerDay<T> {
  return {
    sunday: value,
    monday: value,
    tuesday: value,
    wednesday: value,
    thursday: value,
    friday: value,
    saturday: value,
  };
}

/**
 * An offerable arrival window, in minutes from business-local midnight.
 *
 * Minutes-from-midnight rather than a `Date` or a "2:00pm" string because a
 * window is a rule about the business's wall clock, not an instant: "the
 * 12-2pm window closes at 10am" is true every day, in EST and in EDT alike.
 * Storing an instant would require re-deriving it per day and would drift
 * across the DST boundary; storing a display string would put parsing in the
 * pricing path.
 */
export interface ArrivalWindow {
  /** Stable id, so a booking can record which window the customer took. */
  id: string;
  /** Customer-facing label, e.g. "12:00-2:00pm". */
  label: string;
  startMinutes: number;
  endMinutes: number;
  /**
   * Latest business-local minute of the day at which this window may still be
   * booked. Windows narrow as the day progresses by each one closing in turn.
   */
  cutoffMinutes: number;
  /**
   * Whether the window is offered at all. Absent means yes.
   *
   * Optional, and absent-means-on, because that is what every window stored
   * before this field existed means — the rows carry no `enabled` key and were
   * all being offered. A required field would have made this a migration and
   * made every existing caller that builds a config by hand fail to compile for
   * a property whose only correct value is the default.
   *
   * Kept separate from `days` rather than expressing "off" as an empty `days`:
   * an operator switching a window off for a fortnight and back on should not
   * lose the schedule they set, and "offered on no days" is a mistake worth
   * being able to tell apart from "deliberately paused".
   */
  enabled?: boolean;
  /**
   * The weekdays this window is offered on. Absent means every day.
   *
   * An explicit empty array is *not* the same as absent: it means the window is
   * offered on no day at all, which is inert rather than invalid. See
   * `assertValidPricingConfig` for why that distinction is not a throw.
   */
  days?: DayOfWeek[];
}

/**
 * Whether a window is on offer on a given weekday.
 *
 * The one place the absent-means-everything defaults are read, so the engine,
 * the API projection and the admin preview cannot drift on what a window with
 * no `days` key means.
 */
export function windowOffersDay(window: ArrivalWindow, day: DayOfWeek): boolean {
  if (window.enabled === false) return false;
  if (!Array.isArray(window.days)) return true;
  return window.days.includes(day);
}

/**
 * Same-Day config: which days offer it at all, when it stops for the day, and
 * the windows on offer.
 *
 * Windows remain a single list. Per-day scheduling lives on each window as
 * `days` rather than as one list per weekday: a window is one thing with one id
 * that a booking records, and seven lists would either duplicate that identity
 * or make "the same window" a matter of convention. It also keeps the shape of
 * this interface — and every signature that reads it — unchanged.
 *
 * A day is bookable only when `enabledByDay` allows it *and* some window covers
 * it. Those are different questions: the first is whether the business takes
 * Same-Day at all that day, the second is what time slots exist. Switching a day
 * on with no window covering it yields `all_windows_closed`, which the admin
 * screen surfaces per day rather than leaving to be discovered by a customer.
 */
export interface SameDayConfig {
  enabledByDay: PerDay<boolean>;
  /**
   * Business-local minute of the day past which Same-Day stops being offered
   * entirely, independent of any individual window's cutoff.
   */
  cutoffMinutesByDay: PerDay<number>;
  windows: ArrivalWindow[];
}

/**
 * Which of the three proximity outcomes a booking got.
 *
 * Resolved by the server from bookings near the customer on their service date,
 * and handed to the engine as a value — never computed here. `packages/pricing`
 * has no database and must not appear to have one, the same discipline ADR 0004
 * imposes on Same-Day availability for a different reason.
 *
 * "none" is a real answer and the common one: we have nothing nearby *and* the
 * travel premium is switched off, or the ZIP was never pinned. It is the default
 * precisely so a caller that knows nothing about proximity prices exactly as it
 * did before this axis existed.
 */
export type ProximityTier = "none" | "cluster" | "travel";

/**
 * What the two proximity outcomes are worth, as a percentage of base.
 *
 * The percentages and the switches are separate for the same reason Same-Day's
 * are: an admin who turns the travel premium off for a fortnight should not have
 * to retype 7% to turn it back on, and "off" and "worth nothing" are different
 * claims. A tier priced against a config that does not offer it is a bug in
 * either direction, so the engine throws rather than charging zero — see
 * `getBookingPrice`.
 *
 * Not part of `PublicPricingConfig`, deliberately. Structurally this is Same-Day,
 * not day-of-week surge: a customer cannot derive their tier by changing the form
 * and reading the total, because it depends on other people's bookings. So ADR
 * 0006's "unhideable, therefore published" reasoning does not reach it, and ADR
 * 0009's does — the server returns the resolved tier and its amount for the one
 * date asked about, and the client folds that single answer in with
 * `withProximityTier`.
 */
export interface ProximityConfig {
  clusterEnabled: boolean;
  travelEnabled: boolean;
  /** Taken off when we already have a cleaning near the customer that day. */
  clusterDiscountPercent: number;
  /** Added when the trip is made for this booking alone. */
  travelPremiumPercent: number;
}

/**
 * The full set of admin-configurable pricing inputs for one booking.
 *
 * Deliberately flat and JSON-shaped: this exact object crosses the wire to the
 * booking app and the dashboard. The one non-JSON value is `rateCard`'s
 * `effectiveFrom`; `serializePricingConfig` / `parsePricingConfig` handle it, so
 * no client hand-rolls that conversion.
 */
/**
 * One span of arrival times priced away from the standard rate.
 *
 * `percent` is signed and is a percentage of base like every other adjustment:
 * negative makes the slot cheaper, positive dearer. One signed number rather
 * than a direction plus a magnitude because a rule is one row in the admin and
 * one row in `pricing_config_history`; splitting it would let the two drift and
 * produce a rule that is "10% less" and "more" at the same time.
 *
 * `endMinutes` is EXCLUSIVE. A rule over 8:00-9:00 prices 8:30 and leaves 9:00
 * to the next rule, so two rules that share an edge cannot both fire on the
 * minute between them and charge for it twice.
 */
export interface TimeOfDayRule {
  /** Minutes from midnight, inclusive. */
  startMinutes: number;
  /** Minutes from midnight, exclusive. */
  endMinutes: number;
  /** Signed percentage of base: negative is cheaper, positive is dearer. */
  percent: number;
}

/**
 * The time-of-day rules in force, per day of the week.
 *
 * Its own axis rather than part of `dayOfWeekSurgePercent`: that one prices a
 * whole day, this prices hours within it, and a booking can carry both. They
 * sum, like surge and Same-Day (ADR 0005).
 *
 * Empty everywhere is the off state, and the shape a config that predates the
 * feature parses to — which is why every reader treats a missing list as no
 * rules rather than throwing.
 */
export type TimeOfDayRules = PerDay<TimeOfDayRule[]>;

/**
 * No rules on any day, which is how the axis ships.
 *
 * Not `perDay([])`: that hands all seven days the *same* array, so pushing a
 * rule onto Friday would quietly add it to every other day as well.
 */
export function noTimeOfDayRules(): TimeOfDayRules {
  return {
    sunday: [],
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
  };
}

export interface PricingConfig {
  /**
   * The rate card in force at the booking's rate-card instant. Resolved by the
   * caller, not looked up here — that lookup is a database read on the server
   * and a no-op on the client, which is the whole point of ADR 0003.
   */
  rateCard: RateCard;
  /**
   * Day-of-week surge, as a percentage of base, keyed by the booking's *service
   * date*'s day of week — not its rate-card instant's.
   */
  dayOfWeekSurgePercent: PerDay<number>;
  /** Same-Day surcharge, as a percentage of base, per cleaning type per day. */
  sameDaySurchargePercent: Record<CleaningTypeId, PerDay<number>>;
  /**
   * Flexible Time discount, in cents, per day of week.
   *
   * Cents rather than a percentage, unlike the two surge axes above. The
   * discount is a flat $10 today (`FLEXIBLE_DISCOUNT_CENTS`), and converting it
   * to a percentage would change what every flexible booking is charged as a
   * side effect of making it configurable. Making a value admin-editable and
   * silently repricing it are two changes; this is the first one.
   */
  flexibleDiscountCents: PerDay<Cents>;
  sameDay: SameDayConfig;
  /**
   * Proximity. Not keyed by day of week: whether we are already working near a
   * customer is a fact about one date's bookings, not about Tuesdays.
   */
  proximity: ProximityConfig;
  /** Arrival-time rules, per day. Empty lists mean the axis is off. */
  timeOfDayRules: TimeOfDayRules;
}

/** Both proximity outcomes switched off, which is how the feature ships. */
export function noProximityConfig(): ProximityConfig {
  return {
    clusterEnabled: false,
    travelEnabled: false,
    clusterDiscountPercent: 0,
    travelPremiumPercent: 0,
  };
}

/**
 * Surge axes all zero, Same-Day off, proximity off, Flexible at its current flat
 * $10.
 */
export function zeroSurgeConfig(rateCard: RateCard): PricingConfig {
  return {
    rateCard,
    dayOfWeekSurgePercent: perDay(0),
    sameDaySurchargePercent: CLEANING_TYPE_IDS.reduce((acc, id) => {
      acc[id] = perDay(0);
      return acc;
    }, {} as Record<CleaningTypeId, PerDay<number>>),
    flexibleDiscountCents: perDay(FLEXIBLE_DISCOUNT_CENTS),
    sameDay: {
      enabledByDay: perDay(false),
      cutoffMinutesByDay: perDay(0),
      windows: [],
    },
    proximity: noProximityConfig(),
    timeOfDayRules: noTimeOfDayRules(),
  };
}

/**
 * The config an un-migrated caller gets: the hardcoded rate card for this
 * instant, and no surge at all.
 *
 * This is what makes the config parameter safe to add everywhere at once. A call
 * site that has not been taught to fetch config from the API prices exactly as
 * it did before, rather than pricing at zero or throwing.
 */
export function defaultPricingConfig(rateCardInstant: Date): PricingConfig {
  return zeroSurgeConfig(getRateCard(rateCardInstant));
}

/** Whether any surge-family axis is actually set on this config. */
export function hasAnySurge(config: PricingConfig): boolean {
  const dayOfWeek = DAYS_OF_WEEK.some((day) => config.dayOfWeekSurgePercent[day] !== 0);
  const sameDay = CLEANING_TYPE_IDS.some((id) =>
    DAYS_OF_WEEK.some((day) => (config.sameDaySurchargePercent[id]?.[day] ?? 0) !== 0)
  );
  return dayOfWeek || sameDay;
}

/**
 * Whether pricing on this config can differ by the service date's day of week.
 *
 * The gate on requiring a service date. Making it mandatory outright would break
 * every existing caller — the landing page's "Book from $X" button has no
 * service date to give, and it is a real price. Making it optional
 * unconditionally would let a surge-bearing config silently price a booking at
 * the wrong day's rate. So: required exactly when the answer could differ.
 */
export function pricingVariesByDayOfWeek(config: PricingConfig): boolean {
  if (hasAnySurge(config)) return true;
  const flexible = DAYS_OF_WEEK.map((day) => config.flexibleDiscountCents[day]);
  return flexible.some((cents) => cents !== flexible[0]);
}

/**
 * The same config with every day-dependent axis collapsed, so it can price a
 * booking that has no service date yet.
 *
 * For the funnel's early steps. A customer picks bedrooms before they pick a
 * date, and the cart has to show *something* — but `resolveDayOfWeek` refuses to
 * price a day-varying config without a date, and rightly so. The answer is not to
 * relax that rule but to hand it a config on which the question does not arise:
 * this returns one where `pricingVariesByDayOfWeek` is false by construction, so
 * the engine takes its dateless path instead of throwing.
 *
 * The rate card is untouched. Only the axes that genuinely cannot be resolved
 * without a date are neutralised, so an admin's base-price edit still reaches the
 * cart on the very first step — which is the entire point of serving config to
 * the clients.
 *
 * Surge and Same-Day surcharge go to zero: there is no honest way to charge a
 * day's surge before the customer has chosen the day. The resulting price is
 * therefore a *lower* bound on the surge axis, which is why callers must present
 * it as a "from" price rather than a quote.
 *
 * The Flexible Time discount collapses to the **smallest** value across the week,
 * not the largest. Both remove the variance; the smallest is the one that cannot
 * overstate the discount, so the displayed price is never lowered by a better
 * day's deal that this booking may not get. Understating the surge is covered by
 * the "from" label; overstating a discount would not be.
 */
export function withoutDayOfWeekVariation(config: PricingConfig): PricingConfig {
  const flexibleFloor = Math.min(
    ...DAYS_OF_WEEK.map((day) => config.flexibleDiscountCents[day])
  );
  return {
    ...config,
    dayOfWeekSurgePercent: perDay(0),
    sameDaySurchargePercent: CLEANING_TYPE_IDS.reduce((acc, id) => {
      acc[id] = perDay(0);
      return acc;
    }, {} as Record<CleaningTypeId, PerDay<number>>),
    flexibleDiscountCents: perDay(flexibleFloor),
  };
}

/**
 * The day of the week a service date falls on.
 *
 * Takes the calendar date apart by hand instead of calling `new Date(value)`,
 * which is the bug this function exists to prevent: `new Date("2026-08-16")` is
 * parsed as **UTC** midnight, and `.getDay()` on it in New York reads back
 * Saturday the 15th. Every Sunday booking would have been surged at Saturday's
 * rate — the exact class of timezone error `V5_FROM`'s comment and ADR 0004 are
 * both about.
 *
 * Accepts both stored shapes: `"2026-08-16"` and `"2026-08-16T14:00"`. The
 * dashboard writes the second (`apps/dashboard/utils/form.ts` splits and
 * re-concatenates a time onto `clean_date`), so refusing it would break editing
 * a booking from the admin UI.
 */
export function dayOfWeekForServiceDate(serviceDate: string): DayOfWeek {
  const datePart = String(serviceDate ?? "").trim().split("T")[0];
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (!match) {
    throw new RangeError(
      `Service date must be a YYYY-MM-DD calendar date, got ${JSON.stringify(
        serviceDate
      )}. Surge is resolved from the day of the week the cleaning happens, and ` +
        `guessing that from an unparseable value would surge the wrong day.`
    );
  }
  const [, year, month, day] = match;
  const asUtc = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day))
  );
  // Read back with getUTCDay: the components went in as UTC, so this is the
  // weekday of the calendar date itself, with no timezone applied to it.
  return DAYS_OF_WEEK[asUtc.getUTCDay()];
}

/**
 * Reject a config that cannot be applied, at the point it enters the process,
 * rather than mid-calculation.
 *
 * A surge percentage arrives from an admin form and then from a database row, so
 * it has had two chances to be nonsense by the time an engine sees it. A
 * negative "surge" would quietly discount, and a 10000% one would look like a
 * successful charge for the wrong amount.
 */
/**
 * One day's time-of-day rules, checked at the boundary.
 *
 * The engine refuses these too, but a config is validated where it is built and
 * parsed -- so a bad admin save is rejected as it is written rather than the
 * next time somebody tries to price a booking on that day, which is a failure
 * in front of a customer instead of in front of the person who caused it.
 */
function assertTimeOfDayRules(rules: TimeOfDayRule[] | undefined, day: string): void {
  if (rules === undefined) return;
  if (!Array.isArray(rules)) {
    throw new RangeError(`${day} time-of-day rules must be a list, got ${String(rules)}`);
  }
  for (const rule of rules) {
    assertMinuteOfDay(rule?.startMinutes, `${day} time-of-day rule start`);
    // End is exclusive, so midnight-to-midnight (1440) is a legal whole day.
    if (!Number.isInteger(rule?.endMinutes) || rule.endMinutes < 0 || rule.endMinutes > 1440) {
      throw new RangeError(
        `${day} time-of-day rule end must be a whole minute of the day, got ${String(
          rule?.endMinutes
        )}`
      );
    }
    if (rule.endMinutes <= rule.startMinutes) {
      throw new RangeError(
        `A ${day} time-of-day rule ends at or before it starts (${rule.startMinutes} ` +
          `to ${rule.endMinutes}). A span covering no minute prices nothing, which ` +
          `reads as the rule simply not applying.`
      );
    }
    assertSurgePercent(Math.abs(rule.percent), `${day} time-of-day rule`);
  }
  for (let i = 0; i < rules.length; i++) {
    for (let j = i + 1; j < rules.length; j++) {
      const a = rules[i];
      const b = rules[j];
      if (a.startMinutes < b.endMinutes && b.startMinutes < a.endMinutes) {
        throw new RangeError(
          `Two ${day} time-of-day rules overlap (${a.startMinutes}-${a.endMinutes} and ` +
            `${b.startMinutes}-${b.endMinutes}). One arrival would match both, and ` +
            `charging either is a coin toss.`
        );
      }
    }
  }
}

export function assertValidPricingConfig(config: PricingConfig): PricingConfig {
  if (!config || !config.rateCard) {
    throw new RangeError("Pricing config must include a rate card");
  }
  assertRoomPrices(config.rateCard.roomCents);
  for (const day of DAYS_OF_WEEK) {
    assertSurgePercent(config.dayOfWeekSurgePercent?.[day], `${day} surge`);
    const flexible = config.flexibleDiscountCents?.[day];
    if (!Number.isInteger(flexible) || flexible < 0) {
      throw new RangeError(
        `${day} Flexible Time discount must be a whole number of cents >= 0, got ${String(
          flexible
        )}`
      );
    }
    for (const id of CLEANING_TYPE_IDS) {
      assertSurgePercent(
        config.sameDaySurchargePercent?.[id]?.[day],
        `${id} ${day} Same-Day surcharge`
      );
    }
    assertMinuteOfDay(config.sameDay?.cutoffMinutesByDay?.[day], `${day} Same-Day cutoff`);
    assertTimeOfDayRules(config.timeOfDayRules?.[day], day);
  }
  assertSurgePercent(
    config.proximity?.clusterDiscountPercent,
    "nearby-cleaning discount"
  );
  assertSurgePercent(config.proximity?.travelPremiumPercent, "travel premium");
  for (const window of config.sameDay?.windows ?? []) {
    assertMinuteOfDay(window.startMinutes, `window ${window.id} start`);
    assertMinuteOfDay(window.endMinutes, `window ${window.id} end`);
    assertMinuteOfDay(window.cutoffMinutes, `window ${window.id} cutoff`);
    if (window.endMinutes <= window.startMinutes) {
      throw new RangeError(
        `Arrival window ${window.id} ends at or before it starts (${window.startMinutes}-${window.endMinutes})`
      );
    }
    /**
     * `enabled` and `days` are deliberately not validated here.
     *
     * This function runs on *read* as well as on write — `loadCurrentPricingConfig`
     * and `parsePricingConfig` both call it — so a rule added here turns a bad
     * stored row into a throw inside the pricing endpoint, and one window nobody
     * can book into a funnel nobody can book. A window scheduled on no days, or
     * on a string that is not a weekday, is simply never offered; that is the
     * same containment the cutoff clamp in `resolveSameDayAvailability` uses.
     * The mutation rejects both, which is where a human is present to be told.
     */
  }
  return config;
}

/**
 * A full room table: `MAX_ROOMS` whole-cent prices for each of bedrooms and
 * bathrooms.
 *
 * Exactly that many, not "at least one". A short table leaves the funnel's
 * larger homes with no price, and the engine refuses them — so a table missing
 * its fifth bathroom is every five-bathroom booking failing at checkout, while
 * the cause sits in config nobody is looking at. The upper bound is loose on
 * purpose, as `setRateCard`'s is: it catches a cents/dollars slip, not a
 * pricing opinion.
 */
function assertRoomPrices(prices: RoomPrices | undefined): RoomPrices {
  for (const kind of ["bedroom", "bathroom"] as const) {
    const table = prices?.[kind];
    if (!Array.isArray(table) || table.length !== MAX_ROOMS) {
      throw new RangeError(
        `The ${kind} price table must have exactly ${MAX_ROOMS} entries, got ` +
          `${Array.isArray(table) ? table.length : String(table)}`
      );
    }
    table.forEach((cents, index) => {
      if (!Number.isInteger(cents) || cents < 0 || cents > 1_000_000) {
        throw new RangeError(
          `${kind} ${index + 1} must be priced between $0 and $10,000 in whole ` +
            `cents, got ${String(cents)}`
        );
      }
    });
  }
  return prices as RoomPrices;
}

/** A surge percentage: 0-100, at most two decimal places. */
function assertSurgePercent(percent: number | undefined, label: string): number {
  if (percent === undefined || percent === null) {
    throw new RangeError(`${label} is missing from the pricing config`);
  }
  percentToBasisPoints(percent, label);
  if (percent < 0 || percent > 100) {
    throw new RangeError(`${label} must be between 0 and 100, got ${percent}`);
  }
  return percent;
}

/** Minutes from business-local midnight: 0 through 1440. */
function assertMinuteOfDay(value: number | undefined, label: string): number {
  if (!Number.isInteger(value) || (value as number) < 0 || (value as number) > 1_440) {
    throw new RangeError(
      `${label} must be a whole number of minutes from midnight (0-1440), got ${String(
        value
      )}`
    );
  }
  return value as number;
}

/** The wire shape: `PricingConfig` with the rate card's instant as an ISO string. */
export interface SerializedPricingConfig
  extends Omit<PricingConfig, "rateCard"> {
  rateCard: Omit<RateCard, "effectiveFrom"> & { effectiveFrom: string };
}

export function serializePricingConfig(
  config: PricingConfig
): SerializedPricingConfig {
  return {
    ...config,
    rateCard: {
      ...config.rateCard,
      effectiveFrom: config.rateCard.effectiveFrom.toISOString(),
    },
  };
}

/**
 * What may cross to a browser.
 *
 * See `docs/adr/0006-pricing-config-is-public-integrity-is-not.md`. Per-day
 * prices are unhideable — a customer prices every day of the week by changing
 * the date in the booking form — so the rate card, day-of-week surge and
 * Flexible Time discount are published. Same-Day is not: its surcharge grid and
 * cutoff mechanics are returned per service date alongside the server-resolved
 * availability answer instead, both because publishing cutoffs invites timing
 * them and because ADR 0004 already forbids the client deciding availability.
 */
export interface PublicPricingConfig {
  rateCard: Omit<RateCard, "effectiveFrom"> & { effectiveFrom: string };
  dayOfWeekSurgePercent: PerDay<number>;
  flexibleDiscountCents: PerDay<Cents>;
  /**
   * Published, unlike the Same-Day grid and the proximity tiers beside it.
   *
   * Those are withheld because they depend on what other customers booked or on
   * a surcharge grid the browser must not price from. A time-of-day rule depends
   * on neither: it is a flat statement about the clock, and the booking app
   * cannot print a per-slot price without it.
   */
  timeOfDayRules: TimeOfDayRules;
  /**
   * An opaque fingerprint of the *whole* server-side config this projection was
   * narrowed from — including the Same-Day grid the projection withholds.
   *
   * The client cannot recompute it and is not meant to. It echoes the string
   * back at `/bookings/initialize`, where the server fingerprints the config it
   * just loaded and compares. A mismatch means an admin changed a price between
   * the customer reading their total and submitting it, and the booking is sent
   * back for a re-quote instead of being charged at one number and verified
   * against another. See `pricingConfigFingerprint`.
   */
  configVersion: string;
}

/**
 * Narrow a server-side config to the publishable projection.
 *
 * Written as an explicit field-by-field construction rather than a spread with
 * deletions, so a field added to `PricingConfig` later is *not* published by
 * default. A leak by omission is exactly the failure mode here — the axis that
 * gets added and quietly shipped is the one nobody reviewed.
 */
/**
 * A deep copy of a day's rules, treating a missing or malformed list as no
 * rules. A config serialized before this axis existed has no key at all, and a
 * reader that threw on it would refuse to price every pre-existing booking.
 */
export function copyTimeOfDayRules(rules: TimeOfDayRules | undefined): TimeOfDayRules {
  const copy = noTimeOfDayRules();
  if (!rules) return copy;
  for (const day of DAYS_OF_WEEK) {
    const list = rules[day];
    if (!Array.isArray(list)) continue;
    copy[day] = list.map((rule) => ({
      startMinutes: rule.startMinutes,
      endMinutes: rule.endMinutes,
      percent: rule.percent,
    }));
  }
  return copy;
}

export function toPublicPricingConfig(config: PricingConfig): PublicPricingConfig {
  return {
    rateCard: {
      version: config.rateCard.version,
      effectiveFrom: config.rateCard.effectiveFrom.toISOString(),
      cleaningTypes: { ...config.rateCard.cleaningTypes },
      /**
       * Published, like the cleaning-type prices beside it and for the same
       * reason (ADR 0006): a customer prices every room count by changing the
       * form and reading the total, so there is nothing to hide — and the cart
       * cannot show a five-bedroom price without it.
       */
      roomCents: {
        bedroom: [...config.rateCard.roomCents.bedroom],
        bathroom: [...config.rateCard.roomCents.bathroom],
      },
      supportsDecimals: config.rateCard.supportsDecimals,
    },
    dayOfWeekSurgePercent: { ...config.dayOfWeekSurgePercent },
    flexibleDiscountCents: { ...config.flexibleDiscountCents },
    timeOfDayRules: copyTimeOfDayRules(config.timeOfDayRules),
    configVersion: pricingConfigFingerprint(config),
  };
}

/**
 * A short, stable fingerprint of every value that can move a price.
 *
 * Not a hash for security — it is a change detector, and it is compared against
 * a value this same function produced on the same server minutes earlier. What
 * it must guarantee is that any admin edit changes it, which is why it walks the
 * full config rather than the public projection: a Same-Day surcharge edit is
 * invisible in the projection and very much visible on the customer's card.
 *
 * Canonical by construction — every key is written in a fixed order rather than
 * relying on `JSON.stringify` key ordering — so two servers holding the same
 * config always produce the same string, and a config that only differs by
 * property insertion order never looks like a change.
 */
export function pricingConfigFingerprint(config: PricingConfig): string {
  const parts: string[] = [
    config.rateCard.version,
    config.rateCard.effectiveFrom.toISOString(),
    String(config.rateCard.supportsDecimals),
  ];

  for (const id of CLEANING_TYPE_IDS) {
    parts.push(`ct:${id}=${config.rateCard.cleaningTypes[id] ?? "-"}`);
  }
  parts.push(`rb:${(config.rateCard.roomCents?.bedroom ?? []).join(",")}`);
  parts.push(`ra:${(config.rateCard.roomCents?.bathroom ?? []).join(",")}`);
  for (const day of DAYS_OF_WEEK) {
    parts.push(`dow:${day}=${config.dayOfWeekSurgePercent?.[day] ?? "-"}`);
    parts.push(`flex:${day}=${config.flexibleDiscountCents?.[day] ?? "-"}`);
    parts.push(`sde:${day}=${config.sameDay?.enabledByDay?.[day] ? 1 : 0}`);
    parts.push(`sdc:${day}=${config.sameDay?.cutoffMinutesByDay?.[day] ?? "-"}`);
    for (const id of CLEANING_TYPE_IDS) {
      parts.push(
        `sds:${id}:${day}=${config.sameDaySurchargePercent?.[id]?.[day] ?? "-"}`
      );
    }
  }
  parts.push(`prx:ce=${config.proximity?.clusterEnabled ? 1 : 0}`);
  parts.push(`prx:te=${config.proximity?.travelEnabled ? 1 : 0}`);
  parts.push(`prx:cd=${config.proximity?.clusterDiscountPercent ?? "-"}`);
  parts.push(`prx:tp=${config.proximity?.travelPremiumPercent ?? "-"}`);
  /**
   * Time-of-day rules, sorted by start so a reordered list is not a config
   * change. In the fingerprint because an admin editing a rule between the
   * customer reading their total and submitting it must send the booking back
   * for a re-quote -- which is the entire job of this string.
   */
  for (const day of DAYS_OF_WEEK) {
    const rules = [...(config.timeOfDayRules?.[day] ?? [])].sort(
      (a, b) => a.startMinutes - b.startMinutes
    );
    for (const rule of rules) {
      parts.push(`tod:${day}=${rule.startMinutes}|${rule.endMinutes}|${rule.percent}`);
    }
  }
  // Sorted by id so a reordered `windows` array is not a config change.
  const windows = [...(config.sameDay?.windows ?? [])].sort((a, b) =>
    a.id < b.id ? -1 : a.id > b.id ? 1 : 0
  );
  for (const window of windows) {
    parts.push(
      `win:${window.id}=${window.label}|${window.startMinutes}|${window.endMinutes}` +
        `|${window.cutoffMinutes}|${window.enabled === false ? 0 : 1}` +
        `|${(window.days ?? []).slice().sort().join(",")}`
    );
  }

  return fnv1a(parts.join(";"));
}

/**
 * FNV-1a, 32 bits, run twice over the same input with different offsets and
 * concatenated — 64 bits of hex in a dozen lines, with no dependency and no
 * `crypto` import, because this package runs in the browser bundle as well as on
 * the server. Collisions at this width are not a practical concern for a value
 * whose only job is to differ when a config differs.
 */
function fnv1a(input: string): string {
  const hash = (offset: number): string => {
    let h = offset;
    for (let i = 0; i < input.length; i++) {
      h ^= input.charCodeAt(i);
      // The FNV prime, 16777619, by shift-and-add: `h * 16777619` overflows the
      // exactly-representable range and would lose low bits.
      h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
    return h.toString(16).padStart(8, "0");
  };
  return hash(0x811c9dc5) + hash(0x01000193);
}

/**
 * Enable Same-Day for one service date, at a surcharge the server resolved.
 *
 * The client cannot price a Same-Day booking from published config, deliberately:
 * ADR 0006 withholds the surcharge grid, and the engine refuses a Same-Day booking
 * on a day its config does not offer. But a customer who has just been *offered*
 * Same-Day needs to see what it costs, and the availability response already
 * carries the surcharge for the one date they asked about — which is a single cell,
 * not the grid.
 *
 * So the client folds that answer back into its config and prices through the
 * ordinary path, rather than being handed a separate "add this many cents" input.
 * Keeping the engine config-driven is what stops a second, parallel way of
 * computing a total existing on the client — the failure mode this package was
 * built to end.
 *
 * Only the named day is enabled, and no window cutoffs are added: whether the
 * window is still open stays the server's answer.
 */
export function withSameDayForDate(
  config: PricingConfig,
  serviceDate: string,
  surchargePercent: number,
  cleaningType: CleaningTypeId
): PricingConfig {
  const day = dayOfWeekForServiceDate(serviceDate);
  return assertValidPricingConfig({
    ...config,
    sameDaySurchargePercent: {
      ...config.sameDaySurchargePercent,
      [cleaningType]: {
        ...config.sameDaySurchargePercent[cleaningType],
        [day]: surchargePercent,
      },
    },
    sameDay: {
      ...config.sameDay,
      enabledByDay: { ...config.sameDay.enabledByDay, [day]: true },
      /**
       * A full day, because this config is used only to price a booking the server
       * has already said is offerable. It is not used to decide availability —
       * nothing on the client is.
       */
      cutoffMinutesByDay: { ...config.sameDay.cutoffMinutesByDay, [day]: 1_440 },
    },
  });
}

/**
 * Give one booking the proximity tier the server resolved for it.
 *
 * The counterpart to `withSameDayForDate`, and it exists for the same reason: a
 * customer who has just been *told* they are getting a nearby-cleaning discount
 * needs to see what it is worth, but the client cannot derive it. Density is
 * other people's bookings, and ADR 0009 keeps that off the wire — the server
 * returns the resolved tier and the single percentage that applies to it.
 *
 * Folding that answer back into the config, rather than handing the engine an
 * "and take off this many cents" input, is what keeps one formula in one place.
 * A second way to reach a total is the failure this package exists to end.
 */
export function withProximityTier(
  config: PricingConfig,
  tier: ProximityTier,
  percent: number
): PricingConfig {
  if (tier === "none") return config;
  return assertValidPricingConfig({
    ...config,
    proximity: {
      ...config.proximity,
      ...(tier === "cluster"
        ? { clusterEnabled: true, clusterDiscountPercent: percent }
        : { travelEnabled: true, travelPremiumPercent: percent }),
    },
  });
}

/**
 * Rebuild a priceable config from the public projection, on the client.
 *
 * Same-Day and proximity both come back off: the browser holds no surcharge
 * grid, no cutoffs and no notion of who else is booked nearby, so it cannot
 * compute either — it has to ask. Passing `same_day: true` or a proximity tier
 * to the engine with a config built here throws rather than quietly pricing at
 * zero surcharge, which would look to the customer like a bargain and to the
 * server like a mismatched total.
 */
/**
 * The room table a config arrived with, or the flat one when it arrived without.
 *
 * Absent means "sent by a server that predates configurable room prices" — and
 * such a server charges exactly the flat table, so reading it that way reprices
 * nothing. The alternative, refusing the config, would turn an ordinary deploy
 * where the booking app ships a minute before the API into a checkout that
 * cannot load its prices. A table that IS present is validated in full.
 */
function roomPricesOrFlat(prices: RoomPrices | undefined): RoomPrices {
  return prices ?? flatRoomPrices();
}

export function fromPublicPricingConfig(
  published: PublicPricingConfig
): PricingConfig {
  const effectiveFrom = new Date(published?.rateCard?.effectiveFrom);
  if (Number.isNaN(effectiveFrom.getTime())) {
    throw new RangeError(
      `Published pricing config has an unparseable rate card effectiveFrom: ` +
        `${JSON.stringify(published?.rateCard?.effectiveFrom)}`
    );
  }
  return assertValidPricingConfig({
    rateCard: {
      ...published.rateCard,
      effectiveFrom,
      roomCents: roomPricesOrFlat(published.rateCard.roomCents),
    },
    dayOfWeekSurgePercent: { ...published.dayOfWeekSurgePercent },
    flexibleDiscountCents: { ...published.flexibleDiscountCents },
    sameDaySurchargePercent: CLEANING_TYPE_IDS.reduce((acc, id) => {
      acc[id] = perDay(0);
      return acc;
    }, {} as Record<CleaningTypeId, PerDay<number>>),
    sameDay: {
      enabledByDay: perDay(false),
      cutoffMinutesByDay: perDay(0),
      windows: [],
    },
    proximity: noProximityConfig(),
    timeOfDayRules: copyTimeOfDayRules(published.timeOfDayRules),
  });
}

/**
 * Rehydrate a config that arrived as JSON, and validate it.
 *
 * Clients call this rather than trusting `res.json()`, so a config that a
 * deploy skew or a bad admin edit made invalid fails at the fetch instead of
 * one keystroke later inside a price the customer is looking at.
 */
export function parsePricingConfig(
  serialized: SerializedPricingConfig
): PricingConfig {
  const effectiveFrom = new Date(serialized?.rateCard?.effectiveFrom);
  if (Number.isNaN(effectiveFrom.getTime())) {
    throw new RangeError(
      `Pricing config rate card has an unparseable effectiveFrom: ${JSON.stringify(
        serialized?.rateCard?.effectiveFrom
      )}`
    );
  }
  return assertValidPricingConfig({
    ...serialized,
    rateCard: {
      ...serialized.rateCard,
      effectiveFrom,
      roomCents: roomPricesOrFlat(serialized.rateCard.roomCents),
    },
  });
}
